import React, { useEffect, useState } from 'react'
import { PurchaseDocument, Supplier, InventoryItem } from '../types'

interface FormItem {
  inventoryItemId: string
  quantity: number
  unitPrice: number
  totalPrice: number
}

interface Props {
  title: string
  suppliers: Supplier[]
  inventoryItems: InventoryItem[]
  warehouses?: Array<{ id: string; name: string; isActive: boolean }>
  initial?: PurchaseDocument | null
  onSubmit: (payload: {
    id?: string
    number: string
    date: string
    supplierId: string
    status: 'DRAFT' | 'POSTED' | 'CANCELLED'
    totalAmount: number
    notes?: string
    defaultWarehouseId?: string
    items: FormItem[]
  }) => Promise<void> | void
  onCancel: () => void
}

const PurchaseDocumentForm: React.FC<Props> = ({ title, suppliers, inventoryItems, warehouses = [], initial, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    id: initial?.id,
    number: initial?.number || '',
    date: initial ? initial.date.split('T')[0] : new Date().toISOString().split('T')[0],
    supplierId: initial?.supplierId || '',
    status: (initial?.status as any) || 'DRAFT',
    totalAmount: Number(initial?.totalAmount || 0),
    notes: initial?.notes || '',
    defaultWarehouseId: initial?.defaultWarehouseId || '',
    items: (initial?.items || []).map(it => ({
      inventoryItemId: it.inventoryItemId,
      quantity: Number(it.quantity),
      unitPrice: Number(it.unitPrice),
      totalPrice: Number(it.totalPrice)
    })) as FormItem[]
  })

  useEffect(() => {
    const sum = formData.items.reduce((acc, it) => acc + (Number(it.totalPrice) || 0), 0)
    if (sum !== formData.totalAmount) {
      setFormData(prev => ({ ...prev, totalAmount: Math.round((sum + Number.EPSILON) * 100) / 100 }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items])

  const round2 = (n: number | string) => Math.round((Number(n) + Number.EPSILON) * 100) / 100

  const addItem = () => setFormData(prev => ({
    ...prev,
    items: [...prev.items, { inventoryItemId: '', quantity: 0, unitPrice: 0, totalPrice: 0 }]
  }))

  const removeItem = (index: number) => setFormData(prev => ({
    ...prev,
    items: prev.items.filter((_, i) => i !== index)
  }))

  const updateItem = (index: number, field: keyof FormItem, value: string | number) => setFormData(prev => ({
    ...prev,
    items: prev.items.map((item, i) => {
      if (i !== index) return item
      const updated: any = { ...item }
      if (field === 'quantity') {
        updated.quantity = round2(value)
        updated.totalPrice = round2(Number(updated.quantity) * Number(updated.unitPrice))
      } else if (field === 'unitPrice') {
        updated.unitPrice = round2(value)
        updated.totalPrice = round2(Number(updated.quantity) * Number(updated.unitPrice))
      } else if (field === 'totalPrice') {
        updated.totalPrice = round2(value)
        if (Number(updated.quantity) > 0) updated.unitPrice = round2(Number(updated.totalPrice) / Number(updated.quantity))
      } else if (field === 'inventoryItemId') {
        updated.inventoryItemId = String(value)
      }
      return updated
    })
  }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      id: formData.id,
      number: formData.number,
      date: formData.date,
      supplierId: formData.supplierId,
      status: formData.status as any,
      totalAmount: Number(formData.totalAmount || 0),
      notes: formData.notes,
      defaultWarehouseId: formData.defaultWarehouseId,
      items: formData.items
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Номер документа *</label>
              <input type="text" value={formData.number} onChange={e => setFormData(prev => ({ ...prev, number: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Оставьте пустым для авто-нумерации (INV-PUR-YYYY-####)" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Дата *</label>
              <input type="date" value={formData.date} onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Поставщик *</label>
              <select value={formData.supplierId} onChange={e => setFormData(prev => ({ ...prev, supplierId: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                <option value="">Выберите поставщика</option>
                {suppliers.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
              <select value={formData.status} onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as any }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="DRAFT">Черновик</option>
                <option value="POSTED">Проведен</option>
                <option value="CANCELLED">Отменен</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Общая сумма</label>
              <input type="number" step="0.01" value={Number(formData.totalAmount ?? 0).toFixed(2)} onChange={e => setFormData(prev => ({ ...prev, totalAmount: Number(e.target.value) }))} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Склад</label>
              <select
                value={formData.defaultWarehouseId}
                onChange={(e) => setFormData(prev => ({ ...prev, defaultWarehouseId: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите склад</option>
                {warehouses.filter(w => w.isActive).map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Примечания</label>
            <input
              type="text"
              value={formData.notes}
              onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Позиции документа</label>
              <button type="button" onClick={addItem} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">➕ Добавить позицию</button>
            </div>
            {formData.items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ТМЦ *</label>
                    <select value={item.inventoryItemId} onChange={e => updateItem(index, 'inventoryItemId', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                      <option value="">Выберите ТМЦ</option>
                      {inventoryItems.map(inv => (<option key={inv.id} value={inv.id}>{inv.name} ({inv.unit})</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Количество *</label>
                    <input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(index, 'quantity', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Цена за единицу</label>
                    <input type="number" step="0.01" value={Number(item.unitPrice ?? 0).toFixed(2)} onChange={e => updateItem(index, 'unitPrice', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex items-end space-x-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Сумма</label>
                      <input type="number" step="0.01" value={Number(item.totalPrice ?? 0).toFixed(2)} onChange={e => updateItem(index, 'totalPrice', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <button type="button" onClick={() => removeItem(index)} className="bg-red-600 text-white px-2 py-2 rounded hover:bg-red-700">🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Отмена</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Сохранить</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PurchaseDocumentForm




